import { z } from "zod";

// Expense category enum matching backend
export const ExpenseCategory = {
  Travel: "travel",
  Meals: "meals",
  Lodging: "lodging",
  Parking: "parking",
  Mileage: "mileage",
  Supplies: "supplies",
  Other: "other",
} as const;

export type ExpenseCategoryType =
  (typeof ExpenseCategory)[keyof typeof ExpenseCategory];

// Expense status enum matching backend
export const ExpenseStatus = {
  PendingOrgReview: "pending_org_review",
  Approved: "approved",
  Rejected: "rejected",
} as const;

export type ExpenseStatusType =
  (typeof ExpenseStatus)[keyof typeof ExpenseStatus];

/**
 * Receipt type for display (returned from API)
 */
export interface Receipt {
  id: string;
  path: string;
  fileName: string;
  uploaded: boolean;
}

/**
 * Schema for receipt file input (only fileName needed, path generated by API)
 */
const receiptFileInputSchema = z.object({
  fileName: z.string().min(1, "Receipt file name is required"),
});

/**
 * Schema for creating a new expense
 */
export const createExpenseSchema = z.object({
  jobOfferId: z.string().uuid("Invalid job offer"),
  category: z.enum([
    "travel",
    "meals",
    "lodging",
    "parking",
    "mileage",
    "supplies",
    "other",
  ] as const),
  amount: z
    .string()
    .min(1, "Amount is required")
    .regex(/^\d+(\.\d{1,2})?$/, "Amount must be a valid number (e.g., 50.00)"),
  expenseDate: z
    .string({ required_error: "Expense date is required" })
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  receipts: z
    .array(receiptFileInputSchema)
    .min(1, "At least one receipt is required")
    .max(10, "Maximum 10 receipts allowed"),
  providerNote: z.string().optional(),
});

export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

/**
 * Schema for the expense form (receipts handled separately via file input)
 */
export const createExpenseFormSchema = createExpenseSchema.omit({
  receipts: true,
});

export type CreateExpenseFormInput = z.infer<typeof createExpenseFormSchema>;

/**
 * Schema for updating a rejected expense
 */
export const updateExpenseSchema = z.object({
  expenseId: z.string().uuid(),
  category: z
    .enum([
      "travel",
      "meals",
      "lodging",
      "parking",
      "mileage",
      "supplies",
      "other",
    ] as const)
    .optional(),
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, "Amount must be a valid number (e.g., 50.00)")
    .optional(),
  expenseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
    .optional(),
  newReceipts: z
    .array(receiptFileInputSchema)
    .max(10, "Maximum 10 receipts allowed")
    .optional(),
  providerNote: z.string().optional(),
});

export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

/**
 * Format expense category for display
 */
export function formatExpenseCategory(category: ExpenseCategoryType): string {
  const categoryLabels: Record<ExpenseCategoryType, string> = {
    [ExpenseCategory.Travel]: "Travel",
    [ExpenseCategory.Meals]: "Meals",
    [ExpenseCategory.Lodging]: "Lodging",
    [ExpenseCategory.Parking]: "Parking",
    [ExpenseCategory.Mileage]: "Mileage",
    [ExpenseCategory.Supplies]: "Supplies",
    [ExpenseCategory.Other]: "Other",
  };

  return categoryLabels[category] || category;
}

/**
 * Get all expense categories as options for dropdowns
 */
export function getExpenseCategoryOptions(): Array<{
  value: ExpenseCategoryType;
  label: string;
}> {
  return [
    { value: ExpenseCategory.Travel, label: "Travel" },
    { value: ExpenseCategory.Meals, label: "Meals" },
    { value: ExpenseCategory.Lodging, label: "Lodging" },
    { value: ExpenseCategory.Parking, label: "Parking" },
    { value: ExpenseCategory.Mileage, label: "Mileage" },
    { value: ExpenseCategory.Supplies, label: "Supplies" },
    { value: ExpenseCategory.Other, label: "Other" },
  ];
}

/**
 * Parse a YYYY-MM-DD date string as a local date.
 * Avoids the UTC midnight issue when using new Date() directly.
 */
export function parseExpenseDate(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format expense amount as currency
 */
export function formatCurrency(amount: string | number): string {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numericAmount);
}

/**
 * Format expense status for display
 */
export function formatExpenseStatus(status: ExpenseStatusType): string {
  const statusLabels: Record<ExpenseStatusType, string> = {
    [ExpenseStatus.PendingOrgReview]: "Pending Review",
    [ExpenseStatus.Approved]: "Approved",
    [ExpenseStatus.Rejected]: "Rejected",
  };

  return statusLabels[status] || status;
}

/**
 * Get badge variant for expense status
 */
export function getStatusBadgeVariant(
  status: ExpenseStatusType,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case ExpenseStatus.PendingOrgReview:
      return "secondary";
    case ExpenseStatus.Approved:
      return "default";
    case ExpenseStatus.Rejected:
      return "destructive";
    default:
      return "outline";
  }
}
